import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ApiError,
  formatDate,
  formatMoney,
  formatQuantity,
  type SavedStatementResponse,
  type SavedStatementSummary,
  type StatementReport,
} from '@greencandle/api-client'
import { api } from '../api'

/**
 * FR-B1, FR-B2, FR-B4. Upload an IBKR Activity Statement and keep what reading it produced.
 *
 * <p>The verdict leads, and when it is negative it leads loudly. That ordering is the point of
 * the screen: an import that looks fine and is missing two trades is the expensive outcome, so
 * the reasons it cannot be trusted come before the data it managed to read.
 */
export function ImportPage() {
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)

  const saved = useQuery({
    queryKey: ['statements'],
    queryFn: () => api.savedStatements(),
  })

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadStatement(file),
    onSuccess: (result) => {
      setOpenId(result.summary.id)
      void queryClient.invalidateQueries({ queryKey: ['statements'] })
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteSavedStatement(id),
    onSuccess: (_data, id) => {
      if (openId === id) {
        setOpenId(null)
      }
      void queryClient.invalidateQueries({ queryKey: ['statements'] })
    },
  })

  const opened = useQuery({
    queryKey: ['statement', openId],
    queryFn: () => api.savedStatement(openId!),
    enabled: openId !== null && upload.data?.summary.id !== openId,
  })

  const shown: SavedStatementResponse | undefined =
    upload.data?.summary.id === openId ? upload.data : opened.data

  function accept(files: FileList | null) {
    const file = files?.[0]
    if (file) {
      upload.mutate(file)
    }
  }

  return (
    <>
      <h1 className="page-title">Import</h1>
      <p className="page-subtitle">
        IBKR Activity Statements. Each upload is parsed, checked against the statement's own
        cash totals, and kept.
      </p>

      <div
        className={`dropzone${dragging ? ' dragging' : ''}`}
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          accept(e.dataTransfer.files)
        }}
      >
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf,.pdf"
          className="visually-hidden"
          onChange={(e) => {
            accept(e.target.files)
            // Cleared so re-selecting the same file fires change again - the natural thing to
            // do after a parser fix is upload the very same statement.
            e.target.value = ''
          }}
        />
        <button
          type="button"
          className="primary"
          onClick={() => fileInput.current?.click()}
          disabled={upload.isPending}
        >
          {upload.isPending ? 'Reading…' : 'Load IBKR statement'}
        </button>
        <p className="muted" style={{ margin: '10px 0 0' }}>
          or drop a PDF here
        </p>
      </div>

      {upload.isError && (
        <div className="notice error" role="alert" style={{ marginTop: 16 }}>
          {upload.error instanceof ApiError
            ? (upload.error.problem.detail ?? upload.error.message)
            : 'The upload failed.'}
        </div>
      )}

      {upload.data?.alreadyExisted && (
        <div className="notice" style={{ marginTop: 16 }}>
          This file had already been read. Showing the report stored on{' '}
          {formatDate(upload.data.summary.createdAt)} rather than creating a second one.
        </div>
      )}

      <h2 className="card-title" style={{ marginTop: 28 }}>
        Statements read
      </h2>

      {saved.isPending ? (
        <p className="muted">Loading…</p>
      ) : saved.data && saved.data.length > 0 ? (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>File</th>
                <th>Period</th>
                <th className="right">Trades</th>
                <th className="right">Cash</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {saved.data.map((row) => (
                <SavedRow
                  key={row.id}
                  row={row}
                  open={openId === row.id}
                  onOpen={() => setOpenId(openId === row.id ? null : row.id)}
                  onDelete={() => remove.mutate(row.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="notice">Nothing read yet.</div>
      )}

      {shown && <Report report={shown.report} />}
    </>
  )
}

function SavedRow({
  row,
  open,
  onOpen,
  onDelete,
}: {
  row: SavedStatementSummary
  open: boolean
  onOpen: () => void
  onDelete: () => void
}) {
  return (
    <tr>
      <td>
        <button type="button" className="linklike" onClick={onOpen}>
          {row.filename}
        </button>
        <div className="faint" style={{ fontSize: 12 }}>
          {row.accountId ?? 'no account id'} · read {formatDate(row.createdAt)}
        </div>
      </td>
      <td className="num">
        {row.periodStart ?? '—'} → {row.periodEnd ?? '—'}
      </td>
      <td className="right num">{row.tradeCount}</td>
      <td className="right num">{row.cashFlowCount}</td>
      <td>
        {row.importable ? (
          <span className="pill ok">reconciled</span>
        ) : (
          <span className="pill bad">does not reconcile</span>
        )}
      </td>
      <td className="right">
        <button type="button" onClick={onOpen}>
          {open ? 'Hide' : 'Open'}
        </button>{' '}
        <button type="button" onClick={onDelete}>
          Delete
        </button>
      </td>
    </tr>
  )
}

function Report({ report }: { report: StatementReport }) {
  const ccy = report.header.baseCurrency ?? 'USD'

  return (
    <div style={{ marginTop: 24 }}>
      <div className={`notice ${report.verdict.importable ? 'ok' : 'error'}`}>
        <strong>{report.verdict.headline}</strong>
        {report.verdict.reasons.length > 0 && (
          <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
            {report.verdict.reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="tiles" style={{ marginTop: 16 }}>
        <Tile label="Trades" value={String(report.totals.tradeCount)}
              note={`${report.totals.buyCount} buys · ${report.totals.sellCount} sells`} />
        <Tile label="Cash movements" value={String(report.totals.cashFlowCount)} />
        <Tile label="Instruments" value={String(report.totals.instrumentCount)} />
        <Tile label="Could not read" value={String(report.totals.unparsedCount)}
              bad={report.totals.unparsedCount > 0} />
      </div>

      {/*
        The reconciliation table is the report. Everything else is what the parser thinks it
        read; this is the only part that says whether to believe it.
      */}
      {report.reconciliation.currencies.map((currency) => (
        <div className="card" key={currency.currency} style={{ marginTop: 16 }}>
          <h3 className="card-title">
            Reconciliation · {currency.currency}{' '}
            {currency.balanced ? (
              <span className="pill ok">balances</span>
            ) : (
              <span className="pill bad">does not balance</span>
            )}
          </h3>

          <p className="muted" style={{ marginTop: 0 }}>
            Starting {formatMoney(currency.startingCash, currency.currency)} · ending{' '}
            {formatMoney(currency.endingCash, currency.currency)}
            {!currency.internallyConsistent && (
              <>
                {' '}
                — but its own movements imply{' '}
                {formatMoney(currency.impliedEndingCash, currency.currency)}, so the Cash Report
                itself was misread.
              </>
            )}
          </p>

          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Line</th>
                  <th className="right">Statement</th>
                  <th className="right">Parsed</th>
                  <th className="right">Difference</th>
                </tr>
              </thead>
              <tbody>
                {currency.lines.map((line) => (
                  <tr key={line.label}>
                    <td>{line.label}</td>
                    <td className="right num">
                      {formatMoney(line.statement, currency.currency)}
                    </td>
                    <td className="right num">{formatMoney(line.parsed, currency.currency)}</td>
                    <td className={`right num ${line.balanced ? 'muted' : 'down'}`}>
                      {line.balanced ? '—' : formatMoney(line.difference, currency.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {report.unparsed.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3 className="card-title">Rows that could not be read</h3>
          {/* FR-B4: shown with their raw text, never summarised away. */}
          {report.unparsed.map((row, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <code style={{ fontSize: 12 }}>{row.rawLine}</code>
              <div className="faint" style={{ fontSize: 12 }}>
                page {row.page} · {row.section} · {row.reason}
              </div>
            </div>
          ))}
        </div>
      )}

      <details className="card" style={{ marginTop: 16 }}>
        <summary className="card-title" style={{ cursor: 'pointer' }}>
          Trades ({report.trades.length})
        </summary>
        <div className="table-scroll" style={{ maxHeight: 420, overflowY: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Executed</th>
                <th>Side</th>
                <th className="right">Quantity</th>
                <th className="right">Price</th>
                <th className="right">Proceeds</th>
                <th className="right">Commission</th>
                <th>Codes</th>
              </tr>
            </thead>
            <tbody>
              {report.trades.map((trade, i) => (
                <tr key={`${trade.symbol}-${trade.executedAt}-${i}`}>
                  <td>{trade.symbol}</td>
                  <td className="num">{trade.executedAt.replace('T', ' ')}</td>
                  <td className={trade.side === 'BUY' ? 'up' : 'down'}>{trade.side}</td>
                  <td className="right num">{formatQuantity(trade.quantity)}</td>
                  <td className="right num">{formatMoney(trade.price, trade.currency)}</td>
                  <td className="right num">{formatMoney(trade.proceeds, trade.currency)}</td>
                  <td className="right num">{formatMoney(trade.commission, trade.currency)}</td>
                  <td className="faint">{trade.codes ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <details className="card" style={{ marginTop: 16 }}>
        <summary className="card-title" style={{ cursor: 'pointer' }}>
          Cash movements ({report.cashFlows.length})
        </summary>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Date</th>
                <th>Symbol</th>
                <th>Description</th>
                <th className="right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {report.cashFlows.map((flow, i) => (
                <tr key={i}>
                  <td>
                    <span className="pill">{flow.type}</span>
                  </td>
                  <td className="num">{flow.date}</td>
                  <td>{flow.symbol ?? ''}</td>
                  <td className="muted" style={{ maxWidth: 420 }}>
                    {flow.description}
                  </td>
                  <td className="right num">{formatMoney(flow.amount, flow.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <details className="card" style={{ marginTop: 16 }}>
        <summary className="card-title" style={{ cursor: 'pointer' }}>
          Positions on the statement ({report.positions.length})
        </summary>
        <div className="table-scroll" style={{ maxHeight: 420, overflowY: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Symbol</th>
                <th className="right">Quantity</th>
                <th className="right">Cost basis</th>
                <th className="right">Close</th>
                <th className="right">Value</th>
              </tr>
            </thead>
            <tbody>
              {report.positions.map((position, i) => (
                <tr key={`${position.symbol}-${i}`}>
                  <td>{position.symbol}</td>
                  <td className="right num">{formatQuantity(position.quantity)}</td>
                  <td className="right num">{formatMoney(position.costBasis, ccy)}</td>
                  <td className="right num">{formatMoney(position.closePrice, ccy)}</td>
                  <td className="right num">{formatMoney(position.value, ccy)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  )
}

function Tile({
  label,
  value,
  note,
  bad,
}: {
  label: string
  value: string
  note?: string
  bad?: boolean
}) {
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div className={`stat-value figure${bad ? ' down' : ''}`}>{value}</div>
      {note && (
        <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
          {note}
        </div>
      )}
    </div>
  )
}
